/* eslint-disable ion-eslint-rules/fast-refresh-verifier */
/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
/* eslint-disable ion-eslint-rules/fast-refresh-verifier */
import React from 'react';
import isNil from 'lodash.isnil';
import {
  Peg2Module,
  Peg2ModuleProfile,
  PegParameterConfig,
  PegParameterDefinition,
  PegParameterListGroup,
  PegParameterWrite,
  PegWizardModuleSelection,
  PegWrite
} from '@ion/core/src/types/entities';
// eslint-disable-next-line ion-eslint-rules/fast-refresh-verifier
import {PegCheckboxField} from './components/fields/PegCheckboxField';
// eslint-disable-next-line ion-eslint-rules/fast-refresh-verifier
import {PegNumericSlider} from './components/fields/PegNumericSlider';
// eslint-disable-next-line ion-eslint-rules/fast-refresh-verifier
import {PegNumericStepper} from './components/fields/PegNumericStepper';
// eslint-disable-next-line ion-eslint-rules/fast-refresh-verifier
import {PegTextField} from './components/fields/PegTextField';
import {
  ComposedListOption,
  ComposedParameter,
  ComposedParameterGroup,
  ParameterLabelWithDefault,
  ParameterType,
  PegFieldProps,
  WakeSleepFlatValues,
  WakeSleepValues
} from './types';

const FIELD_COMPONENTS: Record<'bool' | 'str', React.ComponentType<PegFieldProps>> = {
  bool : PegCheckboxField,
  str  : PegTextField
};

const NUM_RANGE_THRESHOLD = 10;

// Numeric parameters are expected to provide min and max per the service contract.
// The ?? 0 fallback is defensive only; undefined bounds default to delta = 0 (Stepper).
const resolveComponent = (def: PegParameterDefinition | undefined): React.ComponentType<PegFieldProps> | undefined => {
  if (def) {
    if (def.type === ParameterType.NUM) {
      const delta = (def.max ?? 0) - (def.min ?? 0);
      return delta <= NUM_RANGE_THRESHOLD ? PegNumericStepper : PegNumericSlider;
    }
    return FIELD_COMPONENTS[def.type];
  }
  return undefined;
};

type ParameterEntries = Record<string, string[]> | Array<Record<string, string[]> | string>;

const getParameterKeys = (parameters: ParameterEntries | undefined): string[] => {
  if (parameters) {
    const entries = Array.isArray(parameters) ? parameters : [parameters];
    return entries.flatMap(function(entry) {
      if (typeof entry === 'string') {
        return entry;
      }
      return Object.entries(entry).flatMap(function([command, fields]) {
        return fields.map((field) => `${command}.${field}`);
      });
    });
  }
};

export const getParameterConfig = (module: Peg2Module): PegParameterConfig | undefined => (
  module.pegWizardModule.parameterConfig ?? module.parameterConfig
);

const getConfiguredParameterKeys = (parameterConfig: PegParameterConfig | undefined): string[] => {
  const labels = parameterConfig?.parameterLabels ?? {};
  return Object.entries(labels).flatMap(function([command, value]) {
    if ('label' in value) {
      return command;
    }
    return Object.keys(value).map((field) => `${command}.${field}`);
  });
};

const getParameterLabel = (module: Peg2Module, key: string): ParameterLabelWithDefault | undefined => {
  const [command, field] = key.split('.', 2);
  const labels = getParameterConfig(module)?.parameterLabels;
  const commandLabels = labels?.[command];

  if (field && commandLabels && !('label' in commandLabels)) {
    return commandLabels[field] as ParameterLabelWithDefault | undefined;
  }

  return labels?.[key] as ParameterLabelWithDefault | undefined;
};

const getParameterDefinition = (module: Peg2Module, key: string): PegParameterDefinition | undefined => {
  const [command, field] = key.split('.', 2);
  return field ? module.pegWizardModule.parameterDefinitions?.[command]?.[field] : undefined;
};

// A numeric default below the parameter's own minimum is out-of-spec data from the
// service - clamping it up guards the wizard even if the two default sources are
// misaligned
const clampToMin = (
  definition: PegParameterDefinition | undefined,
  defaultValue: number | boolean | string
): number | boolean | string => {
  const min                = definition?.min;
  const isNumericParameter = definition?.type === ParameterType.NUM;
  const isBelowMinimum     = !isNil(min) && Number(defaultValue) < Number(min);
  let clamped              = defaultValue;

  if (isNumericParameter && isBelowMinimum) {
    clamped = min as number;
  }

  return clamped;
};

const composeParameter = (module: Peg2Module, key: string): ComposedParameter => {
  const parameterLabel = getParameterLabel(module, key);
  const definition     = getParameterDefinition(module, key);
  const component      = resolveComponent(definition);
  const parameter: ComposedParameter = {
    key,
    label        : parameterLabel?.label ?? key.split('.').pop() ?? key,
    tooltip      : parameterLabel?.tooltip ?? '',
    defaultValue : clampToMin(definition, definition?.defaultValue ?? parameterLabel?.defaultValue ?? '')
  };

  if (parameterLabel?.unit ?? definition?.uom) {
    parameter.unit = parameterLabel?.unit ?? definition?.uom;
  }
  if (definition) {
    parameter.definition = definition;
  };
  if (component) {
    parameter.Component = component;
  };
  if (!isNil(definition?.min)) {
    parameter.min = Number(definition?.min);
  };
  if (!isNil(definition?.max)) {
    parameter.max = Number(definition?.max);
  };
  return parameter;
};

/**
 * Resolves a parameter definition's `pegWrites` into the writes the assembly
 * request expects. A `pegWrite` entry carrying its own `defaultValue` is a
 * dependent constant and is emitted unchanged; every other entry is the
 * editable write and is emitted with the resolved input `value`.
 * Coerces a resolved write value to a number for a `num`-typed parameter, since
 * both the module's own `defaultValue`/pegWrite `defaultValue` and the wizard's
 * input value can arrive as numeric strings (e.g. from raw API JSON) - the
 * assembly API rejects a numeric parameter sent as a string.
 */
const coerceWriteValue = (
  definition: PegParameterDefinition | undefined,
  value: number | boolean | string
): number | boolean | string => (
  definition?.type === ParameterType.NUM ? Number(value) : value
);

/**
 * Resolves a parameter definition's `pegWrites` into the writes the assembly
 * request expects. A `pegWrite` entry carrying its own `defaultValue` is a
 * dependent constant and is emitted unchanged; every other entry is the
 * editable write and is emitted with the resolved input `value`. Either
 * value is coerced to a number for a `num`-typed parameter.
 */
export const resolveWrites = (
  definition: PegParameterDefinition | undefined,
  value: number | boolean | string
): PegParameterWrite[] => (
  (definition?.pegWrites ?? []).map((write) => ({
    parameterId : write.parameterId,
    value       : coerceWriteValue(definition, write.defaultValue ?? value)
  }))
);

/**
 * Groups an Edit PEG Wizard parameter's resolved writes into a per-`parameterId`
 * FIFO queue, so a `pegWrites` entry whose `parameterId` repeats (e.g. two
 * writes packed into the same bitfield parameter) is matched to its own
 * positional value instead of always the first write sharing that id.
 */
const queueValuesByParameterId = (
  writes: PegParameterWrite[]
): Map<number, Array<number | boolean | string>> => (
  writes.reduce((queues, {parameterId, value}) => {
    queues.set(parameterId, [...(queues.get(parameterId) ?? []), value]);
    return queues;
  }, new Map<number, Array<number | boolean | string>>())
);

/**
 * Merges one parameter definition's `pegWrites` with the Edit PEG Wizard
 * file's saved writes for that same parameter, matched by `parameterId`. The
 * definition's own top-level `defaultValue` belongs to the *first* `pegWrites`
 * entry, so that entry's resolved value overwrites `defaultValue` instead of
 * the entry itself; every other entry's resolved value overwrites its own
 * inline `defaultValue`. A `pegWrites` entry with no matching saved write is
 * left unchanged.
 */
const mergeDefinitionWithEditValues = (
  definition: PegParameterDefinition,
  writes: PegParameterWrite[]
): PegParameterDefinition => {
  const queuedValues = queueValuesByParameterId(writes);
  let defaultValue   = definition.defaultValue;

  const pegWrites: PegWrite[] = definition.pegWrites.map((write, index) => {
    const value = queuedValues.get(write.parameterId)?.shift();
    if (value !== undefined) {
      if (index === 0) {
        defaultValue = value;
        return write;
      }
      return {...write, defaultValue: value};
    }
    return write;
  });

  return {...definition, defaultValue, pegWrites};
};

/**
 * Merges a module's `parameterDefinitions` with its Edit PEG Wizard saved
 * `parameterValues`, so `composeModule` resolves each parameter's
 * `defaultValue` to the script's own saved value instead of the module
 * profile's default. Only a `group.label` with a matching array-of-writes
 * entry in `parameterValues` is touched - a group/label the saved script
 * never configured, or a bare-scalar value the `parameterDefinitions`
 * service contract can't represent, is left as-is.
 */
export const mergeParameterDefinitionsWithEditValues = (
  parameterDefinitions: Record<string, Record<string, PegParameterDefinition>>,
  parameterValues: PegWizardModuleSelection['parameterValues'] | undefined
): Record<string, Record<string, PegParameterDefinition>> => {
  if (parameterValues) {
    return Object.fromEntries(
      Object.entries(parameterDefinitions).map(function([group, labels]) {
        const groupValues  = parameterValues[group];
        const mergedLabels = Object.fromEntries(
          Object.entries(labels).map(function([label, definition]) {
            const writes = !Array.isArray(groupValues) && groupValues ? groupValues[label] : undefined;
            return [label, Array.isArray(writes) ? mergeDefinitionWithEditValues(definition, writes) : definition];
          })
        );
        return [group, mergedLabels];
      })
    );
  };
  return parameterDefinitions;
};

/**
 * Merges a Peg2 module profile's `includedModules` with the Edit PEG Wizard
 * file's saved `moduleSelections`, keyed by module name. The profile
 * resolved from `loadPeg2ModuleProfile` supplies Step 2/3's structure
 * (headers, labels, component types); this merges in the edited script's own
 * saved values as each parameter's `defaultValue`, so the same structure
 * renders pre-filled with the script's data instead of the profile's own
 * defaults. A module absent from `moduleSelections` (never configured by
 * this script) is left unchanged. Returns `profile` as-is when either
 * argument is missing (e.g. Create Wizard mode, which has no saved
 * selections to merge).
 */
export const mergeModuleProfileWithEditValues = (
  profile: Peg2ModuleProfile | undefined,
  moduleSelections: Record<string, PegWizardModuleSelection> | undefined
): Peg2ModuleProfile | undefined => {
  if (!profile || !moduleSelections) {
    return profile;
  }

  return {
    ...profile,
    includedModules : profile.includedModules.map(function(module) {
      const selection = moduleSelections[module.pegWizardModule.name];
      if (!selection) {
        return module;
      }
      return {
        ...module,
        pegWizardModule : {
          ...module.pegWizardModule,
          parameterDefinitions : mergeParameterDefinitionsWithEditValues(
            module.pegWizardModule.parameterDefinitions,
            selection.parameterValues
          )
        }
      };
    })
  };
};

/**
 * Extracts the command and its ordered option keys from a
 * `list` group's `parameters` (e.g. `{ignitionMode: ['motion', '3-wire']}`
 * normalizes to `['ignitionMode', ['motion', '3-wire']]`), tolerating the
 * same array-or-object shape `getParameterKeys` normalizes for other groups.
 */
const getListCommand = (parameters: PegParameterListGroup['parameters']): [string, string[]] => {
  const entries               = Array.isArray(parameters) ? parameters : [parameters];
  const commandEntry          = entries.find((entry): entry is Record<string, string[]> => typeof entry !== 'string');
  const [command, optionKeys] = Object.entries(commandEntry ?? {})[0] ?? ['', []];
  return [command, optionKeys];
};

const composeListGroup = (module: Peg2Module, item: PegParameterListGroup): ComposedParameterGroup => {
  const [command, optionKeys] = getListCommand(item.parameters);
  const definitions           = module.pegWizardModule.parameterDefinitions?.[command] ?? {};
  const listOptions: ComposedListOption[] = optionKeys.map((optionKey) => {
    const definition = definitions[optionKey];
    const label      = getParameterLabel(module, `${command}.${optionKey}`);
    return {
      key          : optionKey,
      label        : label?.label ?? optionKey,
      defaultValue : definition?.defaultValue ?? '',
      definition
    };
  });

  return {
    key        : item.key,
    header     : item.header,
    subHeading : item.subHeading,
    type       : item.type,
    command,
    listOptions,
    parameters : []
  };
};

/** Converts the module-profile parameter metadata into controls for the wizard. */
export const composeModule = (module: Peg2Module): ComposedParameterGroup[] => {
  const parameterConfig = getParameterConfig(module);
  const layout = parameterConfig?.parameterGroups;

  if (!layout?.length) {
    const parameterKeys = getConfiguredParameterKeys(parameterConfig);
    return parameterKeys.length ? [{
      key        : 'parameters',
      header     : '',
      parameters : parameterKeys.map((key) => composeParameter(module, key))
    }] : [];
  }

  return layout.map((item) => {
    if (item.type === 'parameter') {
      return {
        key        : item.key,
        header     : '',
        parameters : [composeParameter(module, item.key)]
      };
    }

    if (item.type === ParameterType.LIST) {
      return composeListGroup(module, item);
    }

    return {
      key        : item.key,
      header     : item.header,
      subHeading : item.subHeading,
      type       : item.type,
      parameters : getParameterKeys(item.parameters).map((key) => composeParameter(module, key))
    };
  });
};

/**
 * Flattens a set of composed groups into a single `{parameterKey: defaultValue}`
 * lookup, one entry per parameter across every group. A list group contributes
 * nothing (its `parameters` is always empty - see `composeListGroup`), so its
 * default has to be seeded separately by the caller. Shared by `buildDefaults`
 * (store.ts, one lookup per module) and `buildWakeSleepDefaults` below.
 */
export const flattenGroupDefaults = (
  groups: ComposedParameterGroup[]
): Record<string, number | boolean | string> => (
  groups.reduce((defaults, group) => (
    group.parameters.reduce((groupDefaults, parameter) => (
      {...groupDefaults, [parameter.key]: parameter.defaultValue}
    ), defaults)
  ), {} as Record<string, number | boolean | string>)
);

/**
 * Defaults each `list` group's command to its first option's key- i.e. the dropdown
 * starts on the first option until the user picks one. Every other group contributes
 * nothing (its default lives in `flattenGroupDefaults` instead). Shared by `buildDefaults` (store.ts)
 * and `buildWakeSleepDefaults` below, so a `list` group defaults the same way regardless
 * of which step composed it.
 */
export const getListGroupDefaults = (
  groups: ComposedParameterGroup[]
): Record<string, number | boolean | string> => (
  groups.reduce((defaults, group) => {
    const command     = group.command ?? '';
    const listOptions = group.listOptions ?? [];
    if (group.type === ParameterType.LIST && command && listOptions?.length) {
      return {...defaults, [command]: listOptions[0].key};
    }
    return defaults;
  }, {} as Record<string, number | boolean | string>)
);

/**
 * Builds the initial flat `wakeSleepValues` for a composed wake/sleep module:
 * every ordinary parameter defaults to its own `defaultValue` (via * `flattenGroupDefaults`),
 * and each list group defaults to its first option's key (via `getListGroupDefaults`).
 */
export const buildWakeSleepDefaults = (groups: ComposedParameterGroup[]): WakeSleepFlatValues => {
  const flattenGroups = flattenGroupDefaults(groups);
  const listGroups    = getListGroupDefaults(groups);
  return {...flattenGroups, ...listGroups};
};

/**
 * Resolves each `list` group's actually-selected option from the Edit PEG
 * Wizard file's saved `parameterValues`, overriding `getListGroupDefaults`'s
 * first-option fallback. A script only ever saves the writes for whichever
 * option is active, so the option's key is simply the one label present
 * under the group's `command` in `parameterValues` (e.g. `parameterValues.
 * ignitionMode` holding only a `motion` key means `motion` is selected). A
 * group whose `command` is absent from `parameterValues` - Create Wizard
 * mode, or a group the saved script never configured - is left out, so the
 * caller's own default keeps applying.
 */
export const resolveListGroupSelectionsFromEditValues = (
  groups: ComposedParameterGroup[],
  parameterValues: PegWizardModuleSelection['parameterValues'] | undefined
): WakeSleepFlatValues => {
  let selections = {} as WakeSleepFlatValues;

  if (parameterValues) {
    selections = groups.reduce(function(accumulator, group) {
      let updatedSelections = accumulator;

      if (group.type === ParameterType.LIST && group.command) {
        const commandValues = parameterValues[group.command];
        const selectedOption =
          !Array.isArray(commandValues) && commandValues
            ? Object.keys(commandValues)[0]
            : undefined;

        if (selectedOption) {
          updatedSelections = {
            ...accumulator,
            [group.command] : selectedOption
          };
        }
      }

      return updatedSelections;
    }, {} as WakeSleepFlatValues);
  }

  return selections;
};

const mergeCommandField = (
  values: WakeSleepValues,
  [command, field]: [string, string],
  writes: PegParameterWrite[]
): WakeSleepValues => ({
  ...values,
  [command] : {...(values[command] ?? {}), [field]: writes}
});

/**
 * Merges one list group's currently selected option into `parameterValues`. The
 * selection is read from `values[group.command]` and matched against the group's
 * `listOptions`; the matching option's resolved writes land under
 * `parameterValues[group.command][option.key]`, replacing whatever was there for
 * that command (so switching options never leaves a stale option's writes
 * behind). When nothing matches (no selection yet, or a stale/unknown key),
 * `parameterValues` is returned unchanged.
 */
const resolveListGroupValues = (
  parameterValues: WakeSleepValues,
  group: ComposedParameterGroup,
  values: WakeSleepFlatValues
): WakeSleepValues => {
  const command = group.command ?? '';
  const option  = group.listOptions?.find((listOption) => listOption.key === values[command]);
  if (option) {
    const resolvedWrites = resolveWrites(option.definition, option.defaultValue);
    return mergeCommandField(parameterValues, [command, option.key], resolvedWrites);
  }
  return parameterValues;
};

/**
 * Merges every parameter of one ordinary (non-list) group into `parameterValues`.
 * Each parameter's dotted `key` (`command.field`) splits into the command/field
 * pair its writes are nested under, resolved for the parameter's current value
 * in `values` (falling back to its own `defaultValue` when unset). A bare,
 * non-dotted key has no field to nest under and is skipped, matching
 * `getParameterDefinition`'s existing limitation for standalone parameters.
 */
const resolveFieldGroupValues = (
  parameterValues: WakeSleepValues,
  group: ComposedParameterGroup,
  values: WakeSleepFlatValues
): WakeSleepValues => (
  group.parameters.reduce((groupValues, parameter) => {
    const [command, field] = parameter.key.split('.', 2);
    const value            = values[parameter.key] ?? parameter.defaultValue;
    if (field) {
      const resolvedWrites = resolveWrites(parameter.definition, value);
      return mergeCommandField(groupValues, [command, field], resolvedWrites);
    }
    return groupValues;
  }, parameterValues)
);

/**
 * Translates the wizard's flat `wakeSleepValues` into the nested array-of-writes
 * `parameterValues` shape the assembly request expects (see `resolveWrites`).
 * Reduces over the composed `groups`, delegating each one to
 * `resolveListGroupValues` (for a `list` group's selected option) or
 * `resolveFieldGroupValues` (for every other group's parameters) to merge its
 * writes into the accumulated result.
 */
export const toWakeSleepParameterValues = (
  groups: ComposedParameterGroup[],
  values: WakeSleepFlatValues
): WakeSleepValues => (
  groups.reduce((parameterValues, group) => {
    if (group.type === ParameterType.LIST && group.command) {
      return resolveListGroupValues(parameterValues, group, values);
    }
    return resolveFieldGroupValues(parameterValues, group, values);
  }, {} as WakeSleepValues)
);
